/** 优先核对本页上架草稿的 Adapty 配置缺口，其余目录差异折叠展示；提示不改变保存资格。 */
import type { PaymentPageProductOrderItem } from '@novel-admin/shared/schemas/payment-product-order';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';
import { adaptyApi } from '~/hooks/use-adapty';
import { adaptyProductLinksApi } from '~/hooks/use-adapty-product-links';
import { paymentPagesApi } from '~/hooks/use-payments';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';

const CHANNELS = ['IAP', 'STRIPE'] as const;

type ScopeDifferences = {
  missingFromPaywall: string[];
  unlinked: string[];
  missingLocal: string[];
  unlinkedLocal: string[];
};

/** 范围未获完整证明时不给确定的缺项或通过结论。 */
type ChannelScopeResult = { listed: ScopeDifferences; other: ScopeDifferences } | null;

const LISTED_ROWS: { key: keyof ScopeDifferences; label: string }[] = [
  { key: 'missingFromPaywall', label: 'Adapty 本页未添加' },
  { key: 'unlinkedLocal', label: '尚未关联 Adapty 商品' },
  { key: 'missingLocal', label: '后台缺少商品配置' },
];
const OTHER_ROWS: { key: keyof ScopeDifferences; label: string }[] = [
  { key: 'missingFromPaywall', label: '不在本页 Adapty 中' },
  { key: 'unlinked', label: 'Adapty 商品尚未关联' },
  { key: 'missingLocal', label: 'Adapty 已关联，后台未配置' },
  { key: 'unlinkedLocal', label: '其他后台商品尚未关联' },
];

function ScopeDifferenceRows({
  differences,
  rows,
}: { differences: ScopeDifferences; rows: typeof OTHER_ROWS }) {
  return (
    <dl
      style={{
        margin: '8px 0 0',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 156px) minmax(0, 1fr)',
        columnGap: 10,
        rowGap: 6,
        fontSize: 'var(--text-xs)',
      }}
    >
      {rows.map(({ key, label }) =>
        differences[key].length === 0 ? null : (
          <Fragment key={key}>
            <dt style={{ color: 'var(--fg-muted)', paddingTop: 2 }}>
              {label}
              <span className="mono" style={{ color: 'var(--fg-faint)', marginLeft: 4 }}>
                {differences[key].length}
              </span>
            </dt>
            <dd
              aria-label={label}
              className="hstack"
              style={{ margin: 0, gap: 4, flexWrap: 'wrap' }}
            >
              {differences[key].map((id) => (
                <span
                  key={id}
                  className="chip mono"
                  style={{
                    fontSize: 'var(--text-xs)',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {id}
                </span>
              ))}
            </dd>
          </Fragment>
        ),
      )}
    </dl>
  );
}

export function PaymentProductScopeHints({
  appId,
  scenario,
  platform,
  items,
  selectedAccessLevelIds,
}: {
  appId: string;
  scenario: string;
  platform: string;
  items: PaymentPageProductOrderItem[];
  /** 使用当前草稿；不能以 App 候选全集或保存前的 sort_order 代替本页上架集合。 */
  selectedAccessLevelIds: string[];
}) {
  const role = useAuthStore((state) => state.user?.role);
  const canInspect =
    platform !== 'WEB' && (role === 'operator' || role === 'admin' || role === 'root');
  const catalog = useQuery({
    queryKey: ['adapty', 'catalog', appId],
    queryFn: () => adaptyApi.catalog(appId),
    enabled: canInspect,
    retry: false,
  });
  const links = useQuery({
    queryKey: ['adapty-product-links', appId],
    queryFn: () => adaptyProductLinksApi.list(appId),
    enabled: canInspect,
    retry: false,
  });
  const paywalls = useQuery({
    // 与支付页保存/清空共用App缓存前缀，placement变更会重新读取范围。
    queryKey: ['payment-pages', appId, 'product-scope-paywalls', scenario],
    refetchOnMount: 'always',
    queryFn: async () => {
      const pages = await paymentPagesApi.list(appId);
      const page = pages.find((candidate) => candidate.scenario === scenario);
      return page ? paymentPagesApi.listPaywalls(page.id) : [];
    },
    enabled: canInspect,
    retry: false,
  });
  if (platform === 'WEB') return null;
  const candidates = new Set(items.map((item) => item.access_level_id));
  const selected = new Set(selectedAccessLevelIds);

  function resultOf(channel: (typeof CHANNELS)[number]): ChannelScopeResult {
    const placementId = paywalls.data?.find(
      (row) => row.payment_method === channel,
    )?.adapty_placement_id;
    const placement = catalog.data?.placements.find((row) => row.placement_id === placementId);
    const verified =
      canInspect &&
      catalog.fetchStatus === 'idle' &&
      links.fetchStatus === 'idle' &&
      paywalls.fetchStatus === 'idle' &&
      paywalls.isFetchedAfterMount &&
      !catalog.isError &&
      !links.isError &&
      !paywalls.isError &&
      links.data !== undefined &&
      placement?.is_product_scope_verified === true;
    if (!verified) return null;
    const products = new Set(placement.products.map((product) => product.product_id));
    const related = links.data ?? [];
    const linkedProducts = new Set(related.map((link) => link.adapty_product_id));
    const linkedLevels = new Set(related.map((link) => link.access_level_id));
    // 一个逻辑商品可关联多个A/B变体；paywall包含任一已关联变体即包含该逻辑商品。
    const levelsInPaywall = new Set(
      related
        .filter((link) => products.has(link.adapty_product_id))
        .map((link) => link.access_level_id),
    );
    const outsidePaywall = [...linkedLevels].filter(
      (id) => candidates.has(id) && !levelsInPaywall.has(id),
    );
    const unlinkedLocal = [...candidates].filter((id) => !linkedLevels.has(id));
    return {
      listed: {
        missingFromPaywall: outsidePaywall.filter((id) => selected.has(id)),
        // 未关联的 Adapty UUID 无法可靠归属到本页已选商品，保留在目录详情供人工核实。
        unlinked: [],
        missingLocal: [...selected].filter((id) => !candidates.has(id)),
        unlinkedLocal: unlinkedLocal.filter((id) => selected.has(id)),
      },
      other: {
        missingFromPaywall: outsidePaywall.filter((id) => !selected.has(id)),
        unlinked: [...products].filter((id) => !linkedProducts.has(id)),
        missingLocal: [...levelsInPaywall].filter((id) => !candidates.has(id) && !selected.has(id)),
        unlinkedLocal: unlinkedLocal.filter((id) => !selected.has(id)),
      },
    };
  }

  // 两个通道常挂同一 placement、结果逐项相同；合并成一组展示，避免同一批 ID 整段重复两遍。
  const groups: { channels: string[]; result: ChannelScopeResult }[] = [];
  for (const channel of CHANNELS) {
    const result = resultOf(channel);
    const key = JSON.stringify(result);
    const same = groups.find((group) => JSON.stringify(group.result) === key);
    if (same) same.channels.push(channel);
    else groups.push({ channels: [channel], result });
  }

  return (
    <section
      className="card-flat"
      aria-label="Adapty 商品配置核对"
      style={{ padding: 12, background: 'var(--bg-subtle)', fontSize: 'var(--text-xs)' }}
    >
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Icons.IconInfo size={11} style={{ color: 'var(--fg-faint)' }} />
        <strong>Adapty 商品配置核对</strong>
      </div>
      <p className="muted" style={{ margin: '6px 0 0', lineHeight: 1.6 }}>
        仅核对当前选择上架的商品。上下架调整会即时更新提示，保存后生效；提示不会阻止保存。
      </p>
      {groups.map(({ channels, result }) => {
        const listedGapCount = result
          ? Object.values(result.listed).reduce((count, ids) => count + ids.length, 0)
          : 0;
        const otherDifferenceCount = result
          ? Object.values(result.other).reduce((count, ids) => count + ids.length, 0)
          : 0;
        return (
          <div
            key={channels.join('/')}
            role="group"
            aria-label={channels.join(' / ')}
            style={{ marginTop: 10 }}
          >
            <div className="hstack" style={{ gap: 6 }}>
              {channels.map((channel) => (
                <span key={channel} className="chip mono" style={{ fontSize: 'var(--text-xs)' }}>
                  {channel}
                </span>
              ))}
              {result === null ? <span style={{ color: 'var(--fg-muted)' }}>无法核对</span> : null}
            </div>
            {result === null ? (
              <p className="muted" style={{ margin: '6px 0 0' }}>
                尚未取得完整的 Adapty 配置，暂时不能判断是否存在缺项。
              </p>
            ) : (
              <>
                <p
                  style={{
                    margin: '8px 0 0',
                    color: listedGapCount ? 'var(--warn)' : 'var(--fg-muted)',
                  }}
                >
                  {selected.size === 0
                    ? '尚未选择上架商品，暂不核对本页缺口。'
                    : listedGapCount > 0
                      ? `本页已上架商品有 ${listedGapCount} 项配置待补齐`
                      : `本页已上架的 ${selected.size} 个商品未发现 Adapty 关联或范围缺口。`}
                </p>
                <ScopeDifferenceRows differences={result.listed} rows={LISTED_ROWS} />
                {otherDifferenceCount > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: 'pointer', color: 'var(--fg-muted)' }}>
                      其他商品差异（{otherDifferenceCount} 项）
                    </summary>
                    <p className="muted" style={{ margin: '6px 0 0', lineHeight: 1.6 }}>
                      这些差异未对应本页已上架商品，按需核实即可。不在本页 Adapty
                      中的商品通常属于其他支付页面。
                    </p>
                    <ScopeDifferenceRows differences={result.other} rows={OTHER_ROWS} />
                  </details>
                )}
              </>
            )}
          </div>
        );
      })}
    </section>
  );
}
